import api from './api';

// Cache of URL-based SEO overrides, keyed by normalized path.
let cache = null;
let pending = null;

const normalize = (p) => {
  if (!p) return '/';
  p = String(p).trim();
  if (!p.startsWith('/')) p = '/' + p;
  if (p.length > 1 && p.endsWith('/')) p = p.replace(/\/+$/, '');
  return p || '/';
};

// Fetch & cache the page-SEO map once (shared across components).
export function loadPageSeo() {
  if (cache) return Promise.resolve(cache);
  if (pending) return pending;
  pending = api.get('/seo/public')
    .then(({ data }) => {
      cache = {};
      (data.pages || []).forEach((p) => { cache[normalize(p.path)] = p; });
      return cache;
    })
    .catch(() => { cache = {}; return cache; });
  return pending;
}

// Synchronous lookup — returns the entry if the map is already loaded, else null.
export function getPageSeo(path) {
  if (!cache) return null;
  return cache[normalize(path)] || null;
}

// Stored SEO text (admin-entered or legacy rows) may already contain HTML
// entities like "&amp;". setAttribute / document.title take a raw string, so a
// literal "&amp;" would show verbatim in the tab + crawler snippet. Decode it
// back to plain characters first so titles/descriptions read "&", not "&amp;".
const decodeEntities = (s) => {
  if (s == null) return s;
  const el = document.createElement('textarea');
  el.innerHTML = String(s);
  return el.value;
};

const setMetaName = (name, content) => {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!content) return;
  if (!tag) { tag = document.createElement('meta'); tag.setAttribute('name', name); document.head.appendChild(tag); }
  tag.setAttribute('content', content);
};

const setMetaProp = (property, content) => {
  if (!content) return;
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) { tag = document.createElement('meta'); tag.setAttribute('property', property); document.head.appendChild(tag); }
  tag.setAttribute('content', content);
};

const setLink = (rel, href) => {
  let tag = document.querySelector(`link[rel="${rel}"]`);
  if (!href) { if (tag) tag.remove(); return; }
  if (!tag) { tag = document.createElement('link'); tag.setAttribute('rel', rel); document.head.appendChild(tag); }
  tag.setAttribute('href', href);
};

// Apply a SEO entry (or a product/blog-derived object) to the document head.
export function applySeo(seo) {
  if (!seo) return;
  const title = decodeEntities(seo.title);
  const description = decodeEntities(seo.description);
  if (title) document.title = title;
  setMetaName('description', description);
  setMetaName('keywords', decodeEntities(seo.keywords));
  setMetaName('robots', seo.no_index ? 'noindex, nofollow' : 'index, follow');
  setMetaProp('og:title', title);
  setMetaProp('og:description', description);
  setMetaProp('og:image', seo.og_image);
  setLink('canonical', seo.canonical);
}
